import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, SwitchCamera } from 'lucide-react';
import {
  CameraLayout,
  CropGuide,
  ModeSwitch,
  OverlayButton,
  OverlayLink,
  ShutterButton,
  SideControl,
  type CameraModeChoice,
} from '@/components/capture/CameraLayout';
import { useCamera } from '@/lib/camera';
import { downscaleForUpload } from '@/lib/downscale-image';
import { captureFingerprint, sha256Fingerprint } from '@/lib/fingerprint';
import { runQualityGate, sampleImage } from '@/lib/quality-gate';
import { enqueueCapture } from '@/lib/offline';
import { getSessionId } from '@/lib/session';
import { readStoredWorkflow } from '@/lib/capture-workflows';

/**
 * CA-06: Rapid session (wireframes/capture-app.html #ca06), in the same
 * full-screen camera frame as the viewfinder (mobile-fit.html V-B). "Shoot,
 * next, shoot": the gate still runs per shot, but a failed frame counts
 * toward a retake tally instead of interrupting the walk, matching the note
 * that nothing blocks the camera here. Every shot still enqueues, gate
 * result attached, per SC3 note 3. Every shot joins the job in the route
 * (`/jobs/:jobId/capture/rapid`, CAPTURE_JOBS.md section 6).
 *
 * Each shot goes through `downscaleForUpload` before it is fingerprinted
 * and queued, the same as every other image path in the app
 * (plan/portal-final/INTERFACES.md section 7). The shutter already draws
 * the 3:4 crop at the upload size, so the shrink normally hands the JPEG
 * back untouched; it is the guard for any frame that is not.
 *
 * Nothing is held here: each shot is queued the moment it is taken, so the
 * screen never marks itself busy.
 */
export function RapidSession() {
  const navigate = useNavigate();
  const { jobId = '' } = useParams();
  const jobPath = `/jobs/${encodeURIComponent(jobId)}`;
  const capturePath = `${jobPath}/capture`;
  const { videoRef, ready, error, capturePhoto, canFlip, flip } = useCamera();
  const [saved, setSaved] = useState(0);
  const [needsRetake, setNeedsRetake] = useState(0);
  const [working, setWorking] = useState(false);
  // Burst captures carry the purpose the operator chose on CA-03, so a rapid
  // run extracts for the same purpose as a single shot.
  const [workflow] = useState(readStoredWorkflow);

  // This route is reachable on its own: an installed app reopens on the screen
  // it was left on, and the rapid mode has its own URL. Without the same gate
  // the viewfinder carries, a whole burst extracts with no purpose named.
  useEffect(() => {
    if (!workflow) navigate(`${capturePath}/workflow`, { replace: true });
  }, [workflow, navigate, capturePath]);

  async function shoot() {
    setWorking(true);
    try {
      const shotBlob = await capturePhoto();
      if (!shotBlob) return;
      const blob = await downscaleForUpload(shotBlob);
      const assetFingerprint = await sha256Fingerprint(blob);
      const bitmap = await createImageBitmap(blob);
      const sample = await sampleImage(bitmap);
      const qualityResult = runQualityGate(sample);

      // One shot is one capture here, so the capture's key is derived from
      // its single asset the same way a three-angle capture derives its own.
      // A burst frame is never grouped with the shot before it: the operator
      // is walking a rack, and the next frame is the next item.
      await enqueueCapture({
        captureJobId: jobId,
        mode: 'burst',
        ...(workflow ? { workflow } : {}),
        fingerprint: await captureFingerprint([assetFingerprint]),
        assets: [
          {
            fingerprint: assetFingerprint,
            contentType: blob.type || 'image/jpeg',
            bytes: blob,
          },
        ],
        sessionId: getSessionId(),
        clientQuality: qualityResult,
      });

      setSaved((n) => n + 1);
      if (!qualityResult.passed) setNeedsRetake((n) => n + 1);
    } finally {
      setWorking(false);
    }
  }

  function selectMode(next: CameraModeChoice) {
    if (next === 'burst') return;
    navigate(capturePath, { state: { mode: next } });
  }

  const preview = (
    <>
      <video ref={videoRef} className="absolute inset-0 size-full object-cover" muted playsInline />
      {ready && <CropGuide />}
      {!ready && !error && (
        <p className="absolute inset-0 flex items-center justify-center text-sm text-muted">Starting camera…</p>
      )}
      {error && (
        <p className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-warning">
          {error}
        </p>
      )}
      <div className="absolute inset-x-0 top-0 flex items-start gap-2 p-2.5">
        <OverlayLink to={jobPath} label="Back" icon={ArrowLeft} />
        <div className="flex min-w-0 flex-1 justify-center">
          <p
            aria-live="polite"
            className="flex min-h-11 items-center gap-2 rounded-control border border-border-strong bg-surface/80 px-3 text-xs tabular-nums text-surface-foreground"
          >
            <span>{saved === 1 ? '1 shot' : `${saved} shots`}</span>
            {needsRetake > 0 && <span className="text-warning">{needsRetake} retake</span>}
          </p>
        </div>
        {canFlip ? <OverlayButton label="Switch camera" icon={SwitchCamera} onClick={flip} /> : <span className="size-11" />}
      </div>
    </>
  );

  return (
    <CameraLayout preview={preview}>
      <ModeSwitch active="burst" onSelect={selectMode} />
      <div className="flex items-center justify-between gap-4 sm:justify-start">
        <span className="size-14 shrink-0" aria-hidden="true" />
        <ShutterButton
          label={working ? 'Saving' : 'Shoot'}
          disabled={!ready || working}
          onClick={() => void shoot()}
        />
        <SideControl label="Done" icon={Check} onClick={() => navigate(capturePath)} />
      </div>
    </CameraLayout>
  );
}
